import { useEffect, useRef, useState, useCallback } from "react";
import { Send, FilePenLine, ShieldCheck, ShieldAlert, CheckCircle2 } from "lucide-react";
import { adminApi } from "@/api/admin";
import type { A2AStatus } from "@/types/admin";
import { ApiError } from "@/api/client";
import { tipoPraticaLabel } from "@/lib/constants";
import { AutoInvioSwitch } from "@/components/admin/AutoInvioSwitch";
import { InvioPraticheKpi } from "./InvioPraticheKpi";
import { InvioPraticheFiltri } from "./InvioPraticheFiltri";
import { InvioPraticheTabella } from "./InvioPraticheTabella";
import { PannelloAzioniA2A } from "./PannelloAzioniA2A";
import { PannelloAzioniSentra } from "./PannelloAzioniSentra";
import { PannelloAzioniIren } from "./PannelloAzioniIren";

import {
  fmtDataSolo,
  mapA2ARow, mapSentraRow, mapIrenRow, ZERO_KPI,
} from "./invioPratiche.shared";
import type { UnifiedRow, PanelPrefill, KpiView } from "./invioPratiche.shared";


export function InvioPratiche() {
  const [rows, setRows] = useState<UnifiedRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const LIMIT = 25;
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [kpi, setKpi] = useState<KpiView>(ZERO_KPI);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [fornitoreFilter, setFornitoreFilter] = useState<"" | "A2A" | "Sentra" | "Iren">("");
  const [statoFilter, setStatoFilter] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [commodityFilter, setCommodityFilter] = useState("");
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<PanelPrefill | null>(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  // fornitore che POSSIEDE il job in corso: fissato all'avvio, NON derivato dal pannello
  // aperto (che può cambiare/chiudersi mentre il job gira → polling su endpoint sbagliato).
  const [jobFornitore, setJobFornitore] = useState<"A2A" | "Sentra" | "Iren" | null>(null);
  const [status, setStatus] = useState<A2AStatus | null>(null);
  const [sending, setSending] = useState(false);
  const [runningSubmit, setRunningSubmit] = useState(false);  // true = invio definitivo, false = bozza (A2A e Iren)
  const [annulling, setAnnulling] = useState(false);
  // dialog di conferma riusabile (invio definitivo / annullamento)
  const [confirm, setConfirm] = useState<{ msg: string; danger?: boolean; onYes: () => void } | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "ok" | "err" | "warn" } | null>(null);
  // login OTP relay Sentra: ri-login inline quando un job Sentra fallisce per sessione scaduta.
  // oppId = pratica per cui è partito il login (guardia anti-retry-su-pannello-sbagliato).
  const [sentraLogin, setSentraLogin] = useState<{
    step: "idle" | "starting" | "otp" | "working" | "error";
    loginId?: string; oppId?: string; msg?: string;
  }>({ step: "idle" });
  const [sentraOtp, setSentraOtp] = useState("");
  const sentraLoginPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // selected corrente accessibile dal polling effect senza stale closure (per guardare
  // il refresh in-place del prefill: solo se il pannello aperto è ancora quella pratica).
  const selectedRef = useRef<string | null>(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  function showToast(msg: string, type: "ok" | "err" | "warn") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        offset, limit: LIMIT, order,
        stato: statoFilter, tipo: tipoFilter, commodity: commodityFilter,
        data_da: dataDa, data_a: dataA, q: debouncedQuery,
      };
      if (fornitoreFilter === "A2A") {
        const [res, k] = await Promise.all([adminApi.inviabiliA2A(filters), adminApi.kpiA2A()]);
        setRows(res.rows.map(mapA2ARow));
        setTotal(res.total);
        setKpi(k);
      } else if (fornitoreFilter === "Sentra") {
        const [res, k] = await Promise.all([adminApi.inviabiliSentra(filters), adminApi.kpiSentra()]);
        setRows(res.rows.map(mapSentraRow));
        setTotal(res.total);
        setKpi({ ...k, accettata: 0, attivata: 0, annullata: 0 });
      } else if (fornitoreFilter === "Iren") {
        const [res, k] = await Promise.all([adminApi.inviabiliIren(filters), adminApi.kpiIren()]);
        setRows(res.rows.map(mapIrenRow));
        setTotal(res.total);
        // Iren KPI ha bucket propri intermedi (bozza/attesa_firma/firmata) senza equivalente
        // nella card "Inviate" (che su A2A/Sentra vuol dire "già dispatchata"): stessa scelta
        // di A2A, che non conta la propria "bozza" in nessun bucket KPI. "Inviata" resta 0.
        setKpi({
          da_inviare: k.da_inviare, in_corso: k.in_corso, inviata: 0,
          accettata: k.accettata, attivata: k.attivata, annullata: k.annullata, errore: k.errore,
        });
      } else {
        // Tutti: tre fonti indipendenti, in parallelo.
        // ponytail: pagination su Tutti è best-effort (tre sorgenti paginate separatamente); filtra per fornitore per pagine esatte
        const [a2aRes, sentraRes, irenRes, a2aKpi, sentraKpi, irenKpi] = await Promise.all([
          adminApi.inviabiliA2A(filters), adminApi.inviabiliSentra(filters), adminApi.inviabiliIren(filters),
          adminApi.kpiA2A(), adminApi.kpiSentra(), adminApi.kpiIren(),
        ]);
        const merged = [...a2aRes.rows.map(mapA2ARow), ...sentraRes.rows.map(mapSentraRow), ...irenRes.rows.map(mapIrenRow)].sort((x, y) =>
          order === "desc" ? y.created_at.localeCompare(x.created_at) : x.created_at.localeCompare(y.created_at)
        );
        setRows(merged);
        setTotal(a2aRes.total + sentraRes.total + irenRes.total);
        setKpi({
          da_inviare: a2aKpi.da_inviare + sentraKpi.da_inviare + irenKpi.da_inviare,
          in_corso: a2aKpi.in_corso + sentraKpi.in_corso + irenKpi.in_corso,
          inviata: a2aKpi.inviata + sentraKpi.inviata,
          errore: a2aKpi.errore + sentraKpi.errore + irenKpi.errore,
          // Sentra non ha questi bucket; Iren sì (IrenKpiOut li espone) → somma con A2A.
          accettata: a2aKpi.accettata + irenKpi.accettata,
          attivata: a2aKpi.attivata + irenKpi.attivata,
          annullata: a2aKpi.annullata + irenKpi.annullata,
        });
      }
    } catch {
      showToast("Errore nel caricamento delle pratiche.", "err");
    } finally {
      setLoading(false);
    }
  }, [offset, order, statoFilter, tipoFilter, commodityFilter, dataDa, dataA, debouncedQuery, fornitoreFilter]);

  useEffect(() => { loadList(); }, [loadList]);
  // reset alla prima pagina quando cambia un filtro
  useEffect(() => { setOffset(0); }, [fornitoreFilter, statoFilter, tipoFilter, commodityFilter, dataDa, dataA, debouncedQuery, order]);

  // Polling stato job — endpoint di status dipende dal fornitore del pannello aperto
  // (A2AStatus e SentraStatus hanno la stessa shape).
  useEffect(() => {
    if (!jobId) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    // endpoint scelto dal fornitore PROPRIETARIO del job (jobFornitore), non dal pannello
    // aperto: così chiudere/cambiare pannello a job in volo non fa pollare l'endpoint sbagliato.
    const statusFn = jobFornitore === "Sentra" ? adminApi.sentraStatus
      : jobFornitore === "Iren" ? adminApi.statusIren
      : adminApi.inviaA2AStatus;
    intervalRef.current = setInterval(async () => {
      try {
        const s = await statusFn(jobId);
        setStatus(s);
        if (s.stato !== "in_corso") {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setJobId(null);
          setJobFornitore(null);
          if (s.stato === "completato") {
            if (jobFornitore === "Sentra") {
              const contrattoId = (s.report as Record<string, unknown> | null)?.contratto_id as string | undefined;
              showToast(
                `Contratto Sentra generato${contrattoId ? ` (${contrattoId})` : ""} — link firma pronto. `
                + "Invio SMS/email al cliente da fare manualmente su Akyra.",
                "ok"
              );
              // refresh in place SOLO se il pannello aperto è ancora questa pratica
              if (selectedRef.current === s.opp_id) {
                adminApi.prefillSentra(s.opp_id).then((p) => setPrefill({ ...p, fornitore: "Sentra" })).catch(() => {});
              }
            } else if (jobFornitore === "Iren") {
              showToast(
                runningSubmit
                  ? "Pratica inviata a Iren: email di firma inviata al cliente."
                  : "Bozza creata su Iren. Nessun invio al cliente.",
                "ok"
              );
              if (selectedRef.current === s.opp_id) {
                adminApi.prefillIren(s.opp_id).then((p) => setPrefill({ ...p, fornitore: "Iren" })).catch(() => {});
              }
            } else if (runningSubmit) {
              showToast("Pratica inviata ad A2A.", "ok");
            } else if ((s.report as Record<string, unknown> | null)?.skipped) {
              // bozza già collegata: il backend non ha preparato niente, dirlo «pronta» sarebbe falso
            } else {
              const id = (s.report as Record<string, unknown> | null)?.a2a_opp_id as string | undefined;
              showToast(`Bozza pronta su A2A${id ? ` (${id})` : ""}. Controllala, poi «Invia la bozza».`, "ok");
            }
          } else {
            // Le frasi approvate che cominciano con «Bozza non inviata» sono già complete
            // (causa e via d'uscita): anteporre «Invio fallito» le ripeterebbe.
            const errore = s.errore ?? "errore sconosciuto";
            showToast(errore.startsWith("Bozza non inviata") ? errore : `Invio fallito: ${errore}`, "err");
          }
          loadList();
        }
      } catch { /* keep polling */ }
    }, 1500);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [jobId, loadList, jobFornitore]);

  // cleanup poll login residuo se il componente smonta a metà flusso
  useEffect(() => {
    return () => { if (sentraLoginPollRef.current) clearInterval(sentraLoginPollRef.current); };
  }, []);

  function stopSentraLoginPoll() {
    if (sentraLoginPollRef.current) { clearInterval(sentraLoginPollRef.current); sentraLoginPollRef.current = null; }
  }

  // oppId passato esplicitamente (non letto da sentraLogin state) per evitare closure stale
  // nell'intervallo: l'id non cambia per tutta la durata del login, quindi è sicuro come param.
  function pollSentraLoginStatus(loginId: string, oppId: string, mode: "waiting_otp" | "waiting_completion") {
    stopSentraLoginPoll();
    sentraLoginPollRef.current = setInterval(async () => {
      try {
        const st = await adminApi.sentraLoginStatus(loginId);
        if (mode === "waiting_otp" && st.stato === "attesa_otp") {
          stopSentraLoginPoll();
          setSentraLogin({ step: "otp", loginId, oppId });
          return;
        }
        if (st.stato === "completato") {
          stopSentraLoginPoll();
          setSentraLogin({ step: "idle" });
          setSentraOtp("");
          showToast("Sessione Sentra ripristinata.", "ok");
          setStatus(null);
          // ritenta l'invio solo se il pannello aperto è ancora la pratica che ha innescato il login
          if (selectedRef.current === oppId) handleInviaSentra();
          return;
        }
        if (st.stato === "errore" || st.stato === "timeout") {
          stopSentraLoginPoll();
          setSentraLogin({
            step: "error", loginId, oppId,
            msg: st.errore ?? (st.stato === "timeout" ? "Timeout in attesa dell'OTP." : "Errore durante il login."),
          });
          return;
        }
        // "avvio" (waiting_otp) o "attesa_otp" residuo (waiting_completion): continua a pollare
      } catch { /* keep polling */ }
    }, 1500);
  }

  async function handleSentraLoginStart() {
    if (!prefill || prefill.fornitore !== "Sentra") return;
    const oppId = prefill.opp_id;
    setSentraLogin({ step: "starting", oppId });
    try {
      const res = await adminApi.sentraLoginStart();
      setSentraLogin({ step: "starting", oppId, loginId: res.login_id });
      pollSentraLoginStatus(res.login_id, oppId, "waiting_otp");
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 409
        ? "Login già in corso. Attendere."
        : "Errore nell'avvio del login.";
      setSentraLogin({ step: "error", oppId, msg });
    }
  }

  async function handleSentraLoginOtpSubmit() {
    if (!sentraLogin.loginId || !sentraLogin.oppId) return;
    const { loginId, oppId } = sentraLogin;
    setSentraLogin((s) => ({ ...s, step: "working" }));
    try {
      await adminApi.sentraLoginOtp(loginId, sentraOtp);
      setSentraOtp("");
      pollSentraLoginStatus(loginId, oppId, "waiting_completion");
    } catch {
      setSentraLogin({ step: "error", loginId, oppId, msg: "Errore nell'invio dell'OTP. Riprova." });
    }
  }

  async function openPanelFor(row: UnifiedRow) {
    setSelected(row.opp_id);
    setPrefill(null);
    setStatus(null);
    stopSentraLoginPoll();
    setSentraLogin({ step: "idle" });
    setSentraOtp("");
    setPrefillLoading(true);
    try {
      if (row.fornitore === "Sentra") {
        const p = await adminApi.prefillSentra(row.opp_id);
        setPrefill({ ...p, fornitore: "Sentra" });
      } else if (row.fornitore === "Iren") {
        const p = await adminApi.prefillIren(row.opp_id);
        setPrefill({ ...p, fornitore: "Iren" });
      } else {
        const p = await adminApi.prefillA2A(row.opp_id);
        setPrefill({ ...p, fornitore: "A2A" });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        showToast("Dati pratica incompleti per l'invio.", "warn");
      } else {
        showToast("Errore nel caricamento dei dati pratica.", "err");
      }
      setSelected(null);
    } finally {
      setPrefillLoading(false);
    }
  }

  function closePanel() {
    setSelected(null);
    setPrefill(null);
    setStatus(null);
    stopSentraLoginPoll();
    setSentraLogin({ step: "idle" });
    setSentraOtp("");
  }

  async function handleInvia(submit: boolean, a2aOppIdAtteso: string) {
    if (!prefill || prefill.fornitore !== "A2A") return;
    setSending(true);
    try {
      // submit=true → invio definitivo INCREMENTALE della bozza già preparata (/finalizza,
      // solo segment 7); submit=false → prepara bozza (/invia). Flusso a 2 step.
      const res = submit
        ? await adminApi.finalizzaA2A(prefill.opp_id, a2aOppIdAtteso)
        : await adminApi.inviaA2A(prefill.opp_id, "", false, false, a2aOppIdAtteso);
      if (res.stato === "gia_inviata") {
        showToast(`Gia inviata (A2A ${res.a2a_opp_id}).`, "warn");
        loadList();
      } else if (res.job_id) {
        setJobId(res.job_id);
        setJobFornitore("A2A");
        setRunningSubmit(submit);
        setStatus({ job_id: res.job_id, opp_id: prefill.opp_id, stato: "in_corso", progress: "Avvio…", report: null, errore: null });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.detail === "pagina_non_aggiornata") {
        // la bozza è stata annullata (o creata) altrove: questa pagina è vecchia e il
        // tasto premuto non è più quello giusto. Nessun job è partito.
        showToast("La pratica è cambiata: ricarica la pagina.", "warn");
        loadList();
      } else if (err instanceof ApiError && err.status === 409) {
        // invio_a2a_gia_in_corso: il lock è per utenza, non per pratica — un'altra
        // agenzia sulla stessa utenza fornitore sta già occupando il browser.
        showToast("Invio già in corso su questa utenza", "warn");
      } else {
        showToast("Errore nell'avvio dell'invio. Riprovare.", "err");
      }
    } finally {
      setSending(false);
    }
  }

  async function handleAnnulla() {
    if (!prefill || prefill.fornitore !== "A2A") return;
    setAnnulling(true);
    try {
      const res = await adminApi.annullaA2A(prefill.opp_id);
      if (res.annullata) {
        showToast(`Pratica A2A ${res.a2a_opp_id ?? ""} annullata.`, "ok");
        loadList();
        // refresh IN-PLACE: lo stato torna "da inviare" (id A2A azzerato) → riappare
        // "Prepara bozza" nello stesso pannello, senza chiudi/riapri manuale.
        setStatus(null);
        try { setPrefill({ ...(await adminApi.prefillA2A(prefill.opp_id)), fornitore: "A2A" }); }
        catch { closePanel(); }
      } else {
        showToast("Nessuna pratica A2A da annullare.", "warn");
      }
    } catch {
      showToast("Errore nell'annullamento. Riprovare.", "err");
    } finally {
      setAnnulling(false);
    }
  }

  async function handleInviaSentra() {
    if (!prefill || prefill.fornitore !== "Sentra") return;
    setSending(true);
    try {
      // invia_sms sempre FALSE: nessun contatto automatico al cliente da questo pannello.
      const res = await adminApi.inviaSentra(prefill.opp_id, false, false);
      if (res.stato === "gia_inviata") {
        showToast(`Contratto Sentra gia generato (${res.contratto_id}).`, "warn");
        loadList();
      } else if (res.job_id) {
        setJobId(res.job_id);
        setJobFornitore("Sentra");
        setStatus({ job_id: res.job_id, opp_id: prefill.opp_id, stato: "in_corso", progress: "Avvio…", report: null, errore: null });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showToast("Un invio e gia in corso. Attendere.", "warn");
      } else {
        showToast("Errore nell'avvio dell'invio. Riprovare.", "err");
      }
    } finally {
      setSending(false);
    }
  }

  async function handleAnnullaSentra() {
    if (!prefill || prefill.fornitore !== "Sentra") return;
    setAnnulling(true);
    try {
      const res = await adminApi.annullaSentra(prefill.opp_id);
      showToast(res.note ?? "Stato Sentra azzerato.", res.annullata ? "ok" : "warn");
      loadList();
      setStatus(null);
      try { setPrefill({ ...(await adminApi.prefillSentra(prefill.opp_id)), fornitore: "Sentra" }); }
      catch { closePanel(); }
    } catch {
      showToast("Errore nell'annullamento. Riprovare.", "err");
    } finally {
      setAnnulling(false);
    }
  }

  async function handleInviaIren() {
    if (!prefill || prefill.fornitore !== "Iren") return;
    setSending(true);
    try {
      // Invio definitivo one-shot (spec §5/§6): richiede gli estremi documento identità
      // confermati, bloccato lato servizio da gate_estremi_documento
      // (submit_bloccato_motivo nel prefill, Task 6) se mancanti. `submit` resta un
      // parametro dell'endpoint (lo usano i test), ma dalla UI si invia e basta:
      // la bozza Iren è stata rimossa il 2026-07-31, vedi il commento sui bottoni.
      const res = await adminApi.inviaIren(prefill.opp_id, false, true);
      if (res.stato === "gia_inviata") {
        showToast(`Gia creata (Iren ${res.iren_pratica_id}).`, "warn");
        loadList();
      } else if (res.job_id) {
        setJobId(res.job_id);
        setJobFornitore("Iren");
        setRunningSubmit(true);
        setStatus({ job_id: res.job_id, opp_id: prefill.opp_id, stato: "in_corso", progress: "Avvio…", report: null, errore: null });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showToast("Un invio e gia in corso. Attendere.", "warn");
      } else {
        showToast("Errore nell'avvio dell'invio. Riprovare.", "err");
      }
    } finally {
      setSending(false);
    }
  }

  async function handleAzzeraIren() {
    if (!prefill || prefill.fornitore !== "Iren") return;
    setAnnulling(true);
    try {
      const res = await adminApi.azzeraIren(prefill.opp_id);
      showToast(res.azzerata ? "Stato Iren azzerato." : "Errore nell'azzeramento.", res.azzerata ? "ok" : "warn");
      loadList();
      setStatus(null);
      try { setPrefill({ ...(await adminApi.prefillIren(prefill.opp_id)), fornitore: "Iren" }); }
      catch { closePanel(); }
    } catch {
      showToast("Errore nell'azzeramento. Riprovare.", "err");
    } finally {
      setAnnulling(false);
    }
  }

  const activeFilters = [fornitoreFilter, statoFilter, tipoFilter, commodityFilter, dataDa, dataA].filter(Boolean).length;
  function resetFilters() {
    setFornitoreFilter(""); setStatoFilter(""); setTipoFilter(""); setCommodityFilter(""); setDataDa(""); setDataA("");
  }
  const jobRunning = !!jobId;

  return (
    <div className="space-y-6 max-w-7xl">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">Invio pratiche</h2>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
          beta
        </span>
      </div>

      <AutoInvioSwitch />

      <InvioPraticheKpi kpi={kpi} />

      <InvioPraticheFiltri
        query={query} setQuery={setQuery}
        fornitoreFilter={fornitoreFilter} setFornitoreFilter={setFornitoreFilter}
        statoFilter={statoFilter} setStatoFilter={setStatoFilter}
        tipoFilter={tipoFilter} setTipoFilter={setTipoFilter}
        commodityFilter={commodityFilter} setCommodityFilter={setCommodityFilter}
        dataDa={dataDa} setDataDa={setDataDa}
        dataA={dataA} setDataA={setDataA}
        activeFilters={activeFilters} resetFilters={resetFilters}
      />

      <InvioPraticheTabella
        rows={rows} loading={loading} order={order} setOrder={setOrder}
        offset={offset} setOffset={setOffset} total={total} limit={LIMIT}
        fornitoreFilter={fornitoreFilter} onOpen={openPanelFor}
      />


      {/* Side panel */}
      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end" onClick={closePanel}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative z-10 h-full w-full max-w-md bg-card border-l border-border shadow-xl overflow-y-auto p-6 space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Verifica e invia</h3>
              <button type="button" onClick={closePanel}
                className="text-muted-foreground hover:text-foreground cursor-pointer">&#x2715;</button>
            </div>

            {prefillLoading && <p className="text-sm text-muted-foreground">Caricamento dati...</p>}

            {prefill && (
              <>
                {!prefill.supportato && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-3 py-2.5 text-sm text-red-800">
                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium">Pratica non inviabile</p>
                      {prefill.motivo_non_supportato && (
                        <p className="text-xs mt-0.5 text-red-700">{prefill.motivo_non_supportato}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Stato pratica A2A (visibile nel pannello, non solo in lista) */}
                {prefill.fornitore === "A2A" && (() => {
                  const st = rows.find((r) => r.opp_id === selected)?.stato;
                  if (st !== "bozza" && st !== "inviata") return null;
                  const inviata = st === "inviata";
                  return (
                    <div className={`rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2 ${
                      inviata
                        ? "bg-green-600 text-white border border-green-700"
                        : "bg-sky-600 text-white border border-sky-700"}`}>
                      {inviata ? <Send className="w-4 h-4" /> : <FilePenLine className="w-4 h-4" />}
                      {inviata ? "Pratica inviata ad A2A" : "Bozza pronta su A2A"}
                      {prefill.a2a_opp_id && (
                        <span className="ml-auto font-normal opacity-90 tabular-nums text-xs">{prefill.a2a_opp_id}</span>
                      )}
                    </div>
                  );
                })()}

                {/* Stato pratica Sentra: contratto già generato in precedenza */}
                {prefill.fornitore === "Sentra" && prefill.contratto_id && (
                  <div className="rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2 bg-teal-600 text-white border border-teal-700">
                    <Send className="w-4 h-4" />
                    Contratto Sentra generato
                    <span className="ml-auto font-normal opacity-90 tabular-nums text-xs">{prefill.contratto_id}</span>
                  </div>
                )}

                {/* Stato pratica Iren: bozza/attesa firma/firmata (single-shot, submit=true) */}
                {prefill.fornitore === "Iren" && prefill.iren_pratica_id && (() => {
                  const st = rows.find((r) => r.opp_id === selected)?.stato;
                  if (st !== "bozza" && st !== "attesa_firma" && st !== "firmata") return null;
                  const cls = st === "firmata" ? "bg-green-600 text-white border border-green-700"
                    : st === "attesa_firma" ? "bg-amber-600 text-white border border-amber-700"
                    : "bg-sky-600 text-white border border-sky-700";
                  const label = st === "firmata" ? "Firmata"
                    : st === "attesa_firma" ? "In attesa firma cliente" : "Bozza Iren pronta";
                  return (
                    <div className={`rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2 ${cls}`}>
                      {st === "firmata" ? <CheckCircle2 className="w-4 h-4" /> : <FilePenLine className="w-4 h-4" />}
                      {label}
                      <span className="ml-auto font-normal opacity-90 tabular-nums text-xs">{prefill.iren_pratica_id}</span>
                    </div>
                  );
                })()}

                {/* Canale di firma Iren: evidenziato, il cliente riceve l'email lì */}
                {prefill.fornitore === "Iren" && (
                  <div className="flex items-start gap-2 bg-sky-50 border border-sky-300 rounded-lg px-3 py-2.5 text-sm text-sky-900">
                    <FilePenLine className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium">Il cliente firmerà via email a:</p>
                      <p className="text-sm">{prefill.email_firma || "—"}</p>
                    </div>
                  </div>
                )}

                {/* Estremi documento identità (Fase C): read-only, alimentano il gate submit
                    reale lato backend (gate_estremi_documento, Task 6). */}
                {prefill.fornitore === "Iren" && prefill.documento && (
                  <div className="rounded border p-2 text-sm">
                    <span className="font-medium">Documento:</span>{" "}
                    {prefill.documento.tipo} n. {prefill.documento.numero} ·{" "}
                    rilasciato da {prefill.documento.rilasciato_da} ·{" "}
                    scade {fmtDataSolo(prefill.documento.scadenza)}
                    {prefill.documento.confermato
                      ? <span className="ml-1 text-green-700">✓ confermato dal cliente</span>
                      : <span className="ml-1 text-muted-foreground">non ancora confermato</span>}
                  </div>
                )}
                {prefill.fornitore === "Iren" && prefill.submit_bloccato_motivo && (
                  <p className="text-sm text-muted-foreground">Invio definitivo non disponibile: {prefill.submit_bloccato_motivo}</p>
                )}

                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {prefill.fornitore !== "Iren" && prefill.tipo_cliente && (
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-foreground border border-border font-medium">
                      {prefill.tipo_cliente}
                    </span>
                  )}
                  {prefill.tipo_pratica && (
                    <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                      {tipoPraticaLabel(prefill.tipo_pratica)}
                    </span>
                  )}
                  {prefill.fornitura.tipo_fornitura && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                      {prefill.fornitura.tipo_fornitura}
                    </span>
                  )}
                  {prefill.fornitore !== "Iren" && prefill.metodo_pagamento && (
                    <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                      {prefill.metodo_pagamento}
                    </span>
                  )}
                  {prefill.fornitore === "A2A" && prefill.recapito_bolletta && (
                    <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                      Bolletta: {prefill.recapito_bolletta}
                    </span>
                  )}
                </div>

                <section className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">Anagrafica</h4>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                    <div><div className="text-xs text-muted-foreground">Nome e cognome</div>
                      <div className="text-sm">{prefill.anagrafica.nome} {prefill.anagrafica.cognome}</div></div>
                    <div><div className="text-xs text-muted-foreground">Codice fiscale</div>
                      <div className="text-sm font-mono">{prefill.anagrafica.codice_fiscale || "—"}</div></div>
                    <div><div className="text-xs text-muted-foreground">Email</div>
                      <div className="text-sm">{prefill.anagrafica.email || "—"}</div></div>
                    <div><div className="text-xs text-muted-foreground">Telefono mobile</div>
                      <div className="text-sm">{prefill.anagrafica.telefono || "—"}</div></div>
                    <div className="col-span-2"><div className="text-xs text-muted-foreground">Indirizzo di residenza</div>
                      <div className="text-sm">{prefill.anagrafica.indirizzo_residenza || "—"}</div></div>
                  </div>
                </section>

                <div className="border-t border-border" />

                <section className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">Fornitura</h4>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                    {(() => {
                      const isGas = prefill.fornitura.tipo_fornitura === "Gas";
                      const codice = isGas ? prefill.fornitura.pdr : prefill.fornitura.pod;
                      return (
                        <>
                          <div><div className="text-xs text-muted-foreground">{isGas ? "PDR" : "POD"}</div>
                            <div className="text-sm font-mono text-amber-700">{codice || "—"}</div></div>
                          <div><div className="text-xs text-muted-foreground">Consumo annuo</div>
                            <div className="text-sm text-amber-700">
                              {prefill.fornitura.consumo_annuo_kwh} {isGas ? "Smc/anno" : "kWh/anno"}
                            </div></div>
                        </>
                      );
                    })()}
                    <div className="col-span-2"><div className="text-xs text-muted-foreground">Indirizzo fornitura</div>
                      <div className="text-sm">{prefill.fornitura.indirizzo || "—"}</div></div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    I dati evidenziati provengono dalla bolletta caricata: verificali prima dell'invio.
                  </p>
                </section>

                <div className="border-t border-border" />

                <section className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">Offerta</h4>
                  <div><div className="text-xs text-muted-foreground">Offerta scelta</div>
                    <div className="text-sm">{(prefill.fornitore === "Iren" ? prefill.offerta_nome : prefill.offerta.nome) || "—"}</div></div>
                </section>

                {/* Pagamento: il metodo è nel badge in testa; la sezione serve solo per l'IBAN (SDD).
                    Iren non ha metodo_pagamento/iban nel prefill (v1: solo email di firma). */}
                {prefill.fornitore !== "Iren" && prefill.metodo_pagamento === "SDD" && (
                  <>
                    <div className="border-t border-border" />
                    <section className="space-y-2">
                      <h4 className="text-xs font-semibold uppercase text-muted-foreground">Pagamento</h4>
                      <div><div className="text-xs text-muted-foreground">IBAN (addebito diretto)</div>
                        <div className="text-sm font-mono text-amber-700">{prefill.iban || "—"}</div></div>
                    </section>
                  </>
                )}


                {/* Documento identità — richiesto per l'invio definitivo */}
                <section className="space-y-1">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground">Documento identità</h4>
                  {prefill.has_documento ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-sm bg-green-100 text-green-800">
                      <ShieldCheck className="w-3.5 h-3.5" /> Presente
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-sm bg-amber-100 text-amber-800">
                      <ShieldAlert className="w-3.5 h-3.5" /> Mancante
                    </span>
                  )}
                </section>

                {/* Stato job */}
                {status && (
                  <div className={`rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${
                    status.stato === "in_corso" ? "bg-amber-50 text-amber-800 border border-amber-300"
                    : status.stato === "completato" ? "bg-green-50 text-green-800 border border-green-300"
                    : "bg-red-50 text-red-800 border border-red-300"}`}>
                    {status.stato === "in_corso" && (
                      <span className="h-4 w-4 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
                    )}
                    {status.stato === "completato" && <CheckCircle2 className="w-4 h-4" />}
                    {status.stato === "in_corso" ? (status.progress ?? "In corso…")
                      : status.stato === "completato" ? "Completata"
                      : `Errore: ${status.errore ?? "—"}`}
                  </div>
                )}

                {prefill.fornitore === "A2A" ? (
                  <PannelloAzioniA2A
                    prefill={prefill} rows={rows} selected={selected}
                    sending={sending} annulling={annulling}
                    jobRunning={jobRunning} runningSubmit={runningSubmit}
                    setConfirm={setConfirm}
                    onInvia={handleInvia} onAnnulla={handleAnnulla}
                  />
                ) : prefill.fornitore === "Sentra" ? (
                  <PannelloAzioniSentra
                    prefill={prefill} status={status}
                    sending={sending} annulling={annulling} jobRunning={jobRunning}
                    sentraLogin={sentraLogin} sentraOtp={sentraOtp} setSentraOtp={setSentraOtp}
                    setConfirm={setConfirm}
                    onLoginStart={handleSentraLoginStart} onLoginOtpSubmit={handleSentraLoginOtpSubmit}
                    onInvia={handleInviaSentra} onAnnulla={handleAnnullaSentra}
                  />
                ) : (
                  <PannelloAzioniIren
                    prefill={prefill} rows={rows} selected={selected}
                    sending={sending} annulling={annulling}
                    jobRunning={jobRunning} runningSubmit={runningSubmit}
                    setConfirm={setConfirm}
                    onInvia={handleInviaIren} onAzzera={handleAzzeraIren}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Dialog di conferma (invio definitivo / annullamento) */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
             onClick={() => setConfirm(null)}>
          <div className="bg-background rounded-xl shadow-xl border border-border max-w-sm w-full mx-4 p-5 space-y-4"
               onClick={(e) => e.stopPropagation()}>
            <p className="text-sm">{confirm.msg}</p>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setConfirm(null)}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted cursor-pointer">
                Annulla
              </button>
              <button type="button" onClick={confirm.onYes}
                className={`px-4 py-2 text-sm rounded-lg text-white font-medium cursor-pointer ${
                  confirm.danger ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:opacity-90"}`}>
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg text-sm shadow-lg border ${
          toast.type === "ok" ? "bg-green-100 text-green-800 border-green-200"
          : toast.type === "warn" ? "bg-amber-100 text-amber-800 border-amber-200"
          : "bg-red-100 text-red-800 border-red-200"}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
